/**
 * Badge Component
 *
 * `tone` draws a colour tone (a hero's, a rule's state): its tint and text, or
 * with `variant="outline"` its line and text — whole classes from
 * `TONE_CLASSES`, so no feature hand-rolls a pill.
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { Tone } from '@tonnie/design-tokens';
import { cn } from '@tonnie/foundation';

import { TONE_CLASSES } from '../lib/tone-classes';

export const badgeVariants = cva(
  'inline-flex items-center rounded-pill border px-2.5 py-0.5 text-xs font-semibold transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary/10 text-primary-accessible',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-tone-green-subtle text-tone-green',
        warning: 'border-transparent bg-tone-amber-subtle text-tone-amber',
        destructive: 'border-transparent bg-destructive/10 text-destructive-accessible',
        outline: 'border-border text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Render the badge's look on its single child — a badge that DOES something
   * is `<Badge asChild><button …>`, never a `<Badge onClick>` a keyboard cannot
   * reach (lint: BADGE_NOT_A_CONTROL).
   */
  asChild?: boolean;
  tone?: Tone;
}

function toneClasses(tone: Tone, outline: boolean): string {
  const classes = TONE_CLASSES[tone];
  return outline
    ? cn(classes.line, classes.text)
    : cn('border-transparent', classes.subtle, classes.text);
}

export function Badge({ className, variant, tone, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      className={cn(
        badgeVariants({ variant }),
        tone ? toneClasses(tone, variant === 'outline') : null,
        className
      )}
      data-tone={tone}
      {...props}
    />
  );
}
